import { FunctionComponent } from 'react';

import {
  FontFamilySelectOption,
  ImageNodeAttributes,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonImageUpload,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuSelectHeading,
  MenuSelectTextAlign
} from "mui-tiptap";

import { useAppSettings, useFileUploader, useSnackbar } from '@dw/hooks';
import { useCreateBlobUploadLinkMutation } from '@dw/hooks/api/BlobApi';
import { resizeAndLowerImageFileQuality } from '@dw/services/filePickerService';
import { BLOB_UPLOAD_TYPE } from '@dw/models/blob';
import { DEFAULT_ERROR_MESSAGE } from '@dw/constants';
import { SmartTagMenu } from './tiptap-extensions';
import { IMenuItem } from '@dw/models/shared';
import { FontSizeSelectOption } from 'node_modules/mui-tiptap/dist/cjs/controls/MenuSelectFontSize';

const EMAIL_SAFE_FONT_OPTIONS: FontFamilySelectOption[] = [
  { label: "System Sans", value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
  { label: "Arial / Helvetica", value: 'Arial, Helvetica, sans-serif' },
  { label: "Verdana", value: 'Verdana, Geneva, sans-serif' },
  { label: "Tahoma / Segoe UI", value: 'Tahoma, "Segoe UI", Verdana, sans-serif' },
  { label: "Trebuchet MS", value: '"Trebuchet MS", "Lucida Grande", "Lucida Sans Unicode", sans-serif' },
  { label: "Georgia (Serif)", value: 'Georgia, "Times New Roman", Times, serif' },
  { label: "Times New Roman", value: '"Times New Roman", Times, serif' },
  { label: "Courier New (Monospace)", value: '"Courier New", Courier, monospace' }
] as const;

const FONT_SIZE_OPTIONS: FontSizeSelectOption[] = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 30, 36, 48, 60, 72, 96
].map(x => ({
  value: `${x}px`,
  label: x.toString()
}));

interface IRichTextEditorV2MenuBarProps {
  onUploadingImageStateChanged: (isUploading: boolean) => void;
  smartTags?: IMenuItem[];
  smartTagsOnly?: boolean;
}
export const RichTextEditorV2MenuBar: FunctionComponent<IRichTextEditorV2MenuBarProps> = (props: IRichTextEditorV2MenuBarProps) => {

  const {
    smartTags,
    smartTagsOnly,
    onUploadingImageStateChanged
  } = props;

  const { showSnackbar } = useSnackbar();
  const { appSettings } = useAppSettings();

  const { uploadFile } = useFileUploader();
  const { mutateAsync: createBlobUploadLink } = useCreateBlobUploadLinkMutation();

  const handleImageSelected = async (imageFiles: File[]): Promise<ImageNodeAttributes[]> => {

    if (imageFiles.some(x => !x.type.startsWith('image/'))) {
      showSnackbar('Only image files are supported', 'warning');
      return [];
    }

    onUploadingImageStateChanged(true);
    try {
      const result: ImageNodeAttributes[] = [];
  
      for (const file of imageFiles) {
        const resizedFile = await resizeAndLowerImageFileQuality(file);
        if (!resizedFile) {
          continue;
        }
  
        const uploadLink = await createBlobUploadLink({
          contentType: resizedFile.type,
          fileName: resizedFile.name,
          uploadType: BLOB_UPLOAD_TYPE.DANCE_SCHOOL_EMAIL_ATTACHMENT
        });
        if (!uploadLink) {
          showSnackbar(DEFAULT_ERROR_MESSAGE, 'error');
          return [];
        }
  
        const uploadSuccess = await uploadFile(uploadLink.signedUrl, file, uploadLink.metadata);
        if (!uploadSuccess) {
          showSnackbar(DEFAULT_ERROR_MESSAGE, 'error');
          return [];
        }
  
        result.push({
          src: `${appSettings.blobBaseUrl}/${uploadLink.key}`,
          alt: file.name,
        });
      }
  
      return result;
    } finally {
      onUploadingImageStateChanged(false);
    }
  }

  if (smartTagsOnly && smartTags && smartTags.length > 0) {
    return (
      <MenuControlsContainer>
        <SmartTagMenu
          options={smartTags}
        />
      </MenuControlsContainer>
    )
  } else if (smartTagsOnly) {
    return null;
  }

  return (
    <MenuControlsContainer>
      <MenuSelectFontFamily
        defaultValue={EMAIL_SAFE_FONT_OPTIONS[0].value}
        options={EMAIL_SAFE_FONT_OPTIONS}
      />

      <MenuSelectHeading />

      <MenuSelectFontSize
        options={FONT_SIZE_OPTIONS}
      />

      <MenuDivider />

      <MenuButtonBold />

      <MenuButtonItalic />

      <MenuButtonUnderline />

      <MenuButtonStrikethrough />

      <MenuDivider />

      <MenuButtonTextColor
        defaultTextColor='#000000'
        swatchColors={[
          { value: "#000000", label: "Black" },
          { value: "#ffffff", label: "White" },
          { value: "#888888", label: "Grey" },
          { value: "#ff0000", label: "Red" },
          { value: "#ff9900", label: "Orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#00d000", label: "Green" },
          { value: "#0000ff", label: "Blue" },
        ]}
      />

      <MenuButtonHighlightColor
        swatchColors={[
          { value: "#595959", label: "Dark grey" },
          { value: "#dddddd", label: "Light grey" },
          { value: "#ffa6a6", label: "Light red" },
          { value: "#ffd699", label: "Light orange" },
          // Plain yellow matches the browser default `mark` like when using Cmd+Shift+H
          { value: "#ffff00", label: "Yellow" },
          { value: "#99cc99", label: "Light green" },
          { value: "#90c6ff", label: "Light blue" },
          { value: "#8085e9", label: "Light purple" },
        ]}
      />

      <MenuDivider />

      <MenuSelectTextAlign defaultValue='left' />

      <MenuButtonOrderedList />

      <MenuButtonBulletedList />

      <MenuDivider />

      <MenuButtonEditLink />

      <MenuButtonImageUpload
        inputProps={{
          multiple: false,
          accept: 'image/*'
        }}
        onUploadFiles={handleImageSelected}
      />

      <MenuDivider />

      <MenuButtonRemoveFormatting />

      {smartTags && smartTags.length > 0 && (
        <SmartTagMenu
          options={smartTags}
        />
      )}
    </MenuControlsContainer>
  )
}